import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, HandHeart, Plus, Clock, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import { api, formatGBP } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TrustBadge from "../components/TrustBadge";

const STATUS_MAP = {
  pending: { label: "In review", Icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Live", Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", Icon: XCircle, cls: "bg-red-50 text-red-700 border-red-200" },
  completed: { label: "Completed", Icon: CheckCircle2, cls: "bg-brand-primary-light text-brand-primary border-yellow-200" },
  closed: { label: "Closed", Icon: XCircle, cls: "bg-stone-100 text-stone-600 border-stone-200" },
};

export default function UserDashboard() {
  const { user } = useAuth();
  const [myCampaigns, setMyCampaigns] = useState([]);
  const [myDonations, setMyDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(user?.role === "requester" ? "campaigns" : "donations");

  useEffect(() => {
    (async () => {
      try {
        const [c, d] = await Promise.all([
          api.get("/campaigns/mine").catch(() => ({ data: { items: [] } })),
          api.get("/donations/mine").catch(() => ({ data: { items: [] } })),
        ]);
        setMyCampaigns(c.data.items || []);
        setMyDonations(d.data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (!user) return null;

  const totalGiven = myDonations.filter((d) => d.status === "completed").reduce((s, d) => s + d.amount, 0);
  const totalRaised = myCampaigns.reduce((s, c) => s + (c.amount_raised || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12 sm:py-16">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="eyebrow">Your space</span>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
          <div>
            <h1 className="font-serif-display text-4xl sm:text-5xl leading-tight">
              Hi, <span className="italic text-brand-primary">{user.name.split(" ")[0]}</span>
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <TrustBadge level={user.verification_level || "unverified"} size="lg" />
              <span className="text-sm text-brand-muted">Trust score: <span className="text-brand-primary font-semibold">{user.trust_score || 0}/100</span></span>
            </div>
          </div>
          {(user.role === "requester" || user.role === "admin") && (
            <Link to="/create" className="btn-gold" data-testid="dashboard-create-btn">
              <Plus size={16} strokeWidth={1.8} className="mr-2" /> Start a new campaign
            </Link>
          )}
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
        <div className="premium-card p-6">
          <p className="eyebrow inline-flex items-center gap-2"><Heart size={12} /> Total donated</p>
          <p className="font-serif-display text-3xl mt-2">{formatGBP(totalGiven)}</p>
        </div>
        <div className="premium-card p-6">
          <p className="eyebrow inline-flex items-center gap-2"><HandHeart size={12} /> Donations made</p>
          <p className="font-serif-display text-3xl mt-2">{myDonations.filter(d => d.status === "completed").length}</p>
        </div>
        <div className="premium-card p-6">
          <p className="eyebrow">My campaigns</p>
          <p className="font-serif-display text-3xl mt-2">{myCampaigns.length}</p>
        </div>
        <div className="premium-card p-6">
          <p className="eyebrow">Raised total</p>
          <p className="font-serif-display text-3xl mt-2">{formatGBP(totalRaised)}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-12 border-b border-brand">
        <div className="flex gap-8">
          <button onClick={() => setTab("donations")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${tab === "donations" ? "border-brand-primary text-brand-primary" : "border-transparent text-brand-muted hover:text-brand-text"}`}
            data-testid="tab-donations">
            My donations ({myDonations.length})
          </button>
          {(user.role === "requester" || user.role === "admin") && (
            <button onClick={() => setTab("campaigns")}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${tab === "campaigns" ? "border-brand-primary text-brand-primary" : "border-transparent text-brand-muted hover:text-brand-text"}`}
              data-testid="tab-campaigns">
              My campaigns ({myCampaigns.length})
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="mt-8">
        <DashboardContent
          loading={loading}
          tab={tab}
          myDonations={myDonations}
          myCampaigns={myCampaigns}
        />
      </div>
    </div>
  );
}

function DashboardContent({ loading, tab, myDonations, myCampaigns }) {
  if (loading) return <p className="text-brand-muted">Loading…</p>;
  if (tab === "donations") return <DonationsList donations={myDonations} />;
  return <MyCampaignsList campaigns={myCampaigns} />;
}

function DonationsList({ donations }) {
  if (donations.length === 0) {
    return (
      <div className="premium-card p-12 text-center">
        <Heart className="mx-auto text-brand-primary mb-4" size={28} strokeWidth={1.4} />
        <p className="font-serif-display text-2xl">You haven't donated yet</p>
        <p className="text-brand-muted mt-2">Find a story that moves you and make their day.</p>
        <Link to="/campaigns" className="btn-gold mt-5 inline-flex">Explore campaigns</Link>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {donations.map((d) => (
        <div key={d.id} className="premium-card p-5 flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl overflow-hidden bg-brand-pink flex-shrink-0">
            {d.cover_image_url
              ? <img src={d.cover_image_url} alt="" className="w-full h-full object-cover" />
              : <Heart className="m-auto text-brand-primary w-full h-full p-3.5" strokeWidth={1.6} />}
          </div>
          <div className="flex-1 min-w-0">
            <Link to={`/campaigns/${d.campaign_id}`} className="font-medium text-brand-text hover:text-brand-primary transition-colors inline-flex items-center gap-1">
              {d.campaign_title} <ExternalLink size={12} />
            </Link>
            <p className="text-xs text-brand-muted mt-0.5">
              {new Date(d.created_at).toLocaleDateString("en-GB")} • {d.status === "completed" ? "Paid" : "Pending"}
            </p>
          </div>
          <span className="font-serif-display text-2xl text-brand-primary whitespace-nowrap">{formatGBP(d.amount)}</span>
        </div>
      ))}
    </div>
  );
}

function MyCampaignsList({ campaigns }) {
  if (campaigns.length === 0) {
    return (
      <div className="premium-card p-12 text-center">
        <HandHeart className="mx-auto text-brand-primary mb-4" size={28} strokeWidth={1.4} />
        <p className="font-serif-display text-2xl">No campaigns yet</p>
        <p className="text-brand-muted mt-2">Your story matters. Start your first campaign today.</p>
        <Link to="/create" className="btn-gold mt-5 inline-flex">Start a campaign</Link>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {campaigns.map((c) => {
        const st = STATUS_MAP[c.status] || STATUS_MAP.pending;
        const pct = Math.min(100, Math.round(((c.amount_raised || 0) / Math.max(1, c.amount_needed)) * 100));
        return (
          <div key={c.id} className="premium-card p-6" data-testid={`my-campaign-${c.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 className="font-serif-display text-xl leading-snug flex-1">{c.title}</h3>
              <span className={`inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2.5 py-1 rounded-full border uppercase tracking-wider ${st.cls}`}>
                <st.Icon size={11} strokeWidth={2} />{st.label}
              </span>
            </div>
            <p className="text-sm text-brand-muted line-clamp-2">{c.story}</p>
            <div className="mt-4 w-full h-1.5 bg-brand-pink rounded-full overflow-hidden">
              <div className="h-full bg-brand-primary rounded-full" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between mt-3 text-sm">
              <p className="text-brand-text font-medium">{formatGBP(c.amount_raised || 0)} <span className="text-brand-muted text-xs">/ {formatGBP(c.amount_needed)}</span></p>
              <Link to={`/campaigns/${c.id}`} className="text-brand-primary hover:underline inline-flex items-center gap-1 text-xs">
                View <ExternalLink size={11} />
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}
